import * as React from "react";
import { useHistory } from "react-router-dom";
import {
  CellContext,
  ColumnDef
} from "@tanstack/react-table";
import { useMappedState } from "redux-react-hook";

import Table from "src/core/dashboard/components/widgets/table/Table";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import {
  Transaction,
  useAccountingApi
} from "src/core/accounting/api/accounting.api";
import { transactionTableColumns } from "src/core/shared/components/tables/transactionTableColumns";
import { adminTransactionTableColumns } from "src/core/shared/components/tables/adminTransactionTableColumns";
import type { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";
import ActionButton, { IAction } from "src/library/components/Button/ActionButton";

const TransactionsTab: React.FC = () => {
  const history = useHistory();
  const [ { drop } ] = useDrop();

  // Get current role and terminology for role-based columns
  const { currentRole, terminology } = useMappedState(({ global }: IStore) => ({
    currentRole: global.role,
    terminology: global.settings?.terminology
  }));

  const isAdmin = currentRole?.type === "admin";
  // Use branch-scoped endpoint when drop.id is available (customer users or masquerade mode)
  const [ transactionsResponse, getTransactionsApi ] = useAccountingApi(drop?.customerId ? "LIST_CUSTOMER_TRANSACTIONS" : "LIST_TRANSACTIONS");

  // Follow established pattern - single parameter object
  const onFetchData = React.useCallback(
    (params: { filters: FilterParams<Transaction>; pagination: PaginatedParams<Transaction> }) => {
      if (drop?.id) {
        getTransactionsApi({
          customerId: drop.customerId,
          filters: params.filters,
          pagination: params.pagination
        });
      } else {
        getTransactionsApi({
          filters: params.filters,
          pagination: params.pagination
        });
      }
    },
    [
      drop?.id,
      drop?.customerId,
      getTransactionsApi
    ],
  );

  // Action button renderer for each row - simple button, not dropdown
  const renderActionButton = React.useCallback(
    (info: CellContext<Transaction, unknown>) => {
      const transaction = info.row.original;

      const actions: IAction[] = [
        {
          title: "View",
          action: () => {
            history.push(`${isAdmin ? "/admin" : ""}/accounting/transactions/${transaction.id}`);
          },
          newTabPath: `${isAdmin ? "/admin" : ""}/accounting/transactions/${transaction.id}`
        }
      ];

      return (
        <ActionButton
          title="Actions"
          items={actions}
        />
      );
    },

    [ history, isAdmin ],
  );

  const tableColumns: Array<ColumnDef<Transaction, keyof Transaction>> = React.useMemo(() => {
    // Use admin columns (with  customer/branch) for admin users, customer columns for customer users
    if (isAdmin && terminology) {
      return adminTransactionTableColumns({
        actionButton: renderActionButton,
        terminology
      }) as Array<ColumnDef<Transaction, keyof Transaction>>;
    }

    return transactionTableColumns({
      showActions: true,
      actionButton: renderActionButton,
      terminology
    }) as Array<ColumnDef<Transaction, keyof Transaction>>;
  }, [
    renderActionButton,
    isAdmin,
    terminology
  ]);

  // Extract datafrom backend response structure: { transactions: { result: [], count: 0, pages: 0 } }
  const tableData = React.useMemo(() => {
    return transactionsResponse.data?.transactions?.result || [];
  }, [ transactionsResponse.data?.transactions?.result ]);

  return (
    <Table
      tableOf="Transactions"
      className="large"
      data={tableData}
      onFetchData={onFetchData}
      defaultPagination={{
        offset: 0,
        limit: 20,
        page: 0,
        sort: [ [ "transactionDate", "DESC" ] ] as Array<[string, "DESC" | "ASC"]>
      }}
      loading={transactionsResponse.pending}
      pageCount={transactionsResponse.data?.transactions?.pages || 0}
      rowCount={transactionsResponse.data?.transactions?.count || 0}
      error={transactionsResponse.error}
      columns={tableColumns}
    />
  );
};

export default TransactionsTab;
